import { Avatar, Box, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppImages } from "app/appImages";
import { MprcLink, MprcRequestErrorDialog, MprcRequestIndicator, MprcTextMuted } from "components";
import { AssessmentInstanceDto, AssessmentQuestionDto, RevisionEssayResponsePaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////

interface IProps {
    instance: AssessmentInstanceDto;
    question: AssessmentQuestionDto;
    deployId?: string;
}

export const AssPlayerEssayRevision: React.FC<IProps> = (props) => {

    const { instance, question, deployId } = props;

    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const { showUser } = useModalUserProfile();

    const [loading, setLoading] = useState<boolean>(true);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<RevisionEssayResponsePaginatedList | null>();

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.assPlayer.getRevisionEssaySurvey({
                    instanceId: instance.instanceId ?? null,
                    deployId: deployId ?? null,
                    questionId: question.questionId,
                    pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT
                });
                setViewData(result.data);
            } catch (e) {
                setViewData(null);
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={loading} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {
                viewData && viewData.totalRows > 0 && (
                    <Box>
                        <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            spacing={1}
                        >
                            {viewData.items.map((item, index) => {
                                return (
                                    <Grid key={index} mb={1} size={{ xs: 12 }}>
                                        <Grid
                                            container
                                            justifyContent="center"
                                            alignItems="start"
                                            spacing={1}
                                        >
                                            {
                                                item.identifyUsers &&
                                                <Grid>
                                                    <Avatar
                                                        sx={{ width: 48, height: 48 }}
                                                        src={
                                                            item.thumbnailUrl == null
                                                                ? AppImages.UserThumbnail
                                                                : item.thumbnailUrl
                                                        }
                                                        onClick={() => showUser(item.userId)}
                                                    />
                                                </Grid>
                                            }
                                            <Grid size="grow">
                                                <Grid
                                                    container
                                                    alignItems="center"
                                                    spacing={1}
                                                >
                                                    <Grid>
                                                        {
                                                            item.identifyUsers
                                                                ?
                                                                <MprcLink onClick={() => showUser(item.userId)}>
                                                                    <MprcTextMuted>
                                                                        <b>{item.name}</b>
                                                                    </MprcTextMuted>
                                                                </MprcLink>
                                                                :
                                                                <MprcTextMuted>
                                                                    <b>{t("AssPlayer.Question.Revision.AnonymousUserText", { count: index + 1 })}</b>
                                                                </MprcTextMuted>
                                                        }
                                                        <Typography variant="body2">
                                                            {item.answer}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                );
                            })
                            }
                        </Grid>
                        {
                            viewData && viewData.totalPages > 1 && (
                                <Box display="flex">
                                    <Pagination
                                        shape="rounded"
                                        color="primary"
                                        sx={{ margin: "auto" }}
                                        page={viewData.pageNumber}
                                        count={viewData.totalPages}
                                        onChange={(e, page) => {
                                            if (page != pageNumber) {
                                                setPageNumber(page);
                                                setLoading(true);
                                            }
                                        }}
                                    />
                                </Box>
                            )
                        }
                    </Box>
                )
            }
        </>
    );
};
